import {
  gpsDriveDelete,
  gpsDriveGet,
  gpsDrivePatch,
  gpsDrivePost,
  gpsDriveUpload,
} from "@/lib/api/gpsdrive-client";
import { compressImageFile } from "@/lib/compress-image";
import type { VehicleImageUrls } from "@/types/vehicle-metadata";

// Usuário interno do GPS Drive (tabela própria, id UUID — nunca o
// traccarUserId numérico usado no resto da tela). Exigido pela aba
// PERMISSÕES: GET/PUT /users/:userId/permissions e PATCH /users/:id/role
// usam este id, não o traccarUserId.
// "Grupo do usuário" (pedido 2026-09-03) — papel fixo, SEPARADO do Grupo
// de permissão (roleId). Só decide qual "gaveta" de Grupos de permissão
// aparece pro select (ver gpsdrive-roles.service.ts#RoleScope). "revenda"
// (pedido 2026-09-07) nunca é aceito por changeAccountType abaixo — só sai
// de createReseller, que cria a empresa filha junto (ver
// CompaniesService#createReseller no backend).
export const ACCOUNT_TYPES = ["admin", "usuario", "funcionario", "motorista", "revenda"] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export interface GpsDriveUserIdentity {
  id: string;
  companyId: string;
  roleId: string | null;
  accountType: AccountType;
}

// Fonte real da tela de vínculo usuário↔veículo (PermissionLinkPicker em
// vehicle-access-dialog.tsx e routes/index.tsx) — GET /api/v1/users do GPS
// Drive Backend, NUNCA GET /api/users direto no Traccar (ver
// src/services/users.service.ts, que continua existindo só para a tela de
// administração "Usuários" em routes/users.tsx — CRUD completo contra o
// Traccar, sem relação com isolamento por empresa, propositalmente
// intocado). O Traccar não tem conceito de empresa (uma conta
// administrator vê todo mundo); o isolamento é resolvido inteiramente no
// GPS Drive Backend antes da resposta chegar aqui — ver
// gpsdrive-backend/src/users/users.repository.ts.
export interface LinkableUser {
  id: number;
  name: string;
  email: string;
  // "minha_empresa": já provisionado na empresa do chamador — vínculo
  // normal. "nao_provisionado": ainda não foi reivindicado por nenhuma
  // empresa — precisa continuar aparecendo, é assim que o primeiro vínculo
  // é descoberto (ver PermissionsRepository#provisionUserFromTraccar).
  // Um usuário já provisionado em OUTRA empresa nunca chega até aqui.
  status: "minha_empresa" | "nao_provisionado";
}

export interface UserAvatarResponse {
  imageUrls: VehicleImageUrls | null;
}

// GET /api/v1/companies/me (revenda, pedido 2026-09-07) — empresa do
// próprio usuário autenticado, atrás de authGuard normal (nunca
// platformAuthGuard — ver gpsdrive-companies.service.ts, que é só o
// painel Super Master). parentCompanyId != null = a própria empresa já é
// uma revenda, usado só pra esconder a opção "Revenda" no popup de novo
// usuário (único nível permitido). `modules` (pedido 2026-09-16) — Agenda/
// Câmera ativados pelo Super Master, usado por app-sidebar.tsx pra decidir
// se mostra esses itens de menu.
export interface MyCompany {
  id: string;
  name: string;
  parentCompanyId: string | null;
  modules: { agenda: boolean; camera: boolean; frota?: boolean };
}

export interface CreateResellerInput {
  traccarUserId: number;
  companyName: string;
}

export interface ResellerResult {
  company: MyCompany;
  user: GpsDriveUserIdentity;
}

export const gpsDriveUsersService = {
  listLinkable: () => gpsDriveGet<LinkableUser[]>("/api/v1/users"),

  // Avatar do usuário do Traccar — sem relação com empresa (ver header do
  // model UserAvatar no backend): qualquer empresa que edite esse usuário
  // vê e pode trocar o mesmo avatar, é uma propriedade da pessoa.
  getAvatar: (traccarUserId: number) =>
    gpsDriveGet<UserAvatarResponse>(`/api/v1/users/${traccarUserId}/avatar`),
  // Comprime antes de enviar (2026-10-01): foto direto da câmera do
  // iPhone passava de 5 MB (STORAGE_MAX_FILE_SIZE_MB) e o upload falhava.
  uploadAvatar: async (traccarUserId: number, file: File) =>
    gpsDriveUpload<UserAvatarResponse>(
      `/api/v1/users/${traccarUserId}/avatar`,
      await compressImageFile(file),
    ),
  removeAvatar: (traccarUserId: number) =>
    gpsDriveDelete<void>(`/api/v1/users/${traccarUserId}/avatar`),

  // Resolve (e provisiona, se for a primeira vez) o id interno a partir
  // do traccarUserId — é assim que a aba PERMISSÕES descobre qual userId
  // usar. Restrito a Administrator/Manager no backend.
  resolveByTraccarId: (traccarUserId: number) =>
    gpsDriveGet<GpsDriveUserIdentity>(`/api/v1/users/by-traccar/${traccarUserId}`),

  // Troca o Grupo (Role) do usuário — nunca sobrescreve as permissões
  // individuais já configuradas (ver gpsDriveUserPermissionsService#
  // restoreDefaults para a ação explícita de restaurar os padrões).
  changeRole: (userId: string, roleId: string) =>
    gpsDrivePatch<GpsDriveUserIdentity>(`/api/v1/users/${userId}/role`, { roleId }),

  // "Grupo do usuário" (pedido 2026-09-03) — independente de changeRole
  // acima, nunca mexe em roleId/permissões. Nunca aceita "revenda" (ver
  // MUTABLE_ACCOUNT_TYPES no backend) — use createReseller abaixo.
  changeAccountType: (userId: string, accountType: Exclude<AccountType, "revenda">) =>
    gpsDrivePatch<GpsDriveUserIdentity>(`/api/v1/users/${userId}/account-type`, { accountType }),

  // Revenda (pedido 2026-09-07) — empresa do usuário autenticado, só pra
  // decidir se a opção "Revenda" aparece no popup de novo usuário (único
  // nível: uma revenda não pode criar sub-revenda).
  getMyCompany: () => gpsDriveGet<MyCompany>("/api/v1/companies/me"),

  // Cria a empresa filha de revenda + vincula o traccarUserId (já criado
  // no Traccar pelo próprio CreateUserDialog, mesmo passo de qualquer
  // usuário novo) como o primeiro Administrator dela.
  createReseller: (input: CreateResellerInput) =>
    gpsDrivePost<ResellerResult>("/api/v1/companies/reseller", input),

  // "Situação" (tela Usuários, pedido 2026-09-12) — checado antes de
  // reativar um usuário suspenso, pra decidir se mostra o aviso de
  // "acesso de confiança" (ver grantTrustAccess abaixo). userId aqui é
  // sempre o id interno (UUID) do GPS Drive Backend, resolvido via
  // resolveByTraccarId, nunca o traccarUserId.
  overdueStatus: (userId: string) =>
    gpsDriveGet<{ hasOverdueInvoice: boolean }>(`/api/v1/users/${userId}/overdue-status`),

  // Suspender/Reativar (coluna Situação) — SOFT, nunca mexe no Traccar
  // (revisão 2026-09-12 do mesmo dia: bloquear no Traccar impedia até o
  // login, sem chance de o cliente ver o aviso de pendência). Só muda o
  // que GET /users/me/billing-status devolve pro app do cliente.
  setSuspended: (userId: string, suspended: boolean) =>
    gpsDrivePatch<GpsDriveUserIdentity>(`/api/v1/users/${userId}/suspended`, { suspended }),

  // Reativa o acesso mesmo com fatura vencida, por `days` dias —
  // TrustAccessEnforcer (backend) suspende de novo sozinho se o prazo
  // passar sem pagamento.
  grantTrustAccess: (userId: string, days: number) =>
    gpsDrivePost<{ trustAccessUntil: string | null }>(`/api/v1/users/${userId}/trust-access`, { days }),
};
